"use strict";

(() => {
  const DESIGNER_SELECTOR = "koolezt-pag-designer";
  const FRAME_SELECTOR = ".koolezt-pag-designer__frame";
  const DESIGNER_ORIGIN = "https://create.koolezt.com";
  const MOBILE_QUERY = "(max-width: 749px)";
  const READY_MESSAGE = "pag-design-preview-ready";
  const INVALIDATED_MESSAGE = "pag-design-preview-invalidated";
  const RENDER_READY_MESSAGE = "pag-design-preview-render-ready";
  // The designer draws each change in the browser at once; the preview frame shows that drawing until the print is ready.
  const LOCAL_PREVIEW_MESSAGE = "pag-design-local-preview";
  const EXTERNAL_PREVIEW_MESSAGE = "pag-designer-external-preview";
  const PHONE_PREVIEW_MESSAGE = "pag-designer-phone-preview";
  const STUDIO_MESSAGE = "pag-designer-studio";
  const STUDIO_HOST_MESSAGE = "pag-designer-studio-host";
  // Main product photo area, tried in order (Yuva, Dawn-style themes, generic).
  const GALLERY_SELECTORS = [
    ".product-detail-slider.main-slider-wrapper",
    ".product-no-media",
    "media-gallery .product__media-list",
    ".product__media-wrapper",
    ".product-gallery",
    ".product__media"
  ];

  let designer = null;
  let sourceFrame = null;
  let popup = null;
  let previewFrame = null;
  let restoreButton = null;
  let previewReady = false;
  let renderReady = false;
  let dismissed = false;
  let requestId = "";
  let frameRequestedFor = "";
  let scheduled = false;
  let externalPreviewEnabled = false;
  // Set per Artwork in Koolezt OS ("Preview on phone"); the designer sends it when it opens.
  // drawer: pinned pop-up while the customer edits. gallery: the design covers the main product photo,
  // floating: a preview frame pinned at the top of the screen (Macorner style). off: no product-page preview; the designer shows its own.
  let phonePreview = "drawer";
  let gallery = null;
  let galleryOverlay = null;
  let galleryForMobile = null;
  // Product gallery mode: the designer sends the picture it drew, shown here as a plain image (no second designer page).
  let galleryImageUrl = null;
  // No-AI designs (the designer says instant: true): the Preview button opens this page-level window with the latest
  // browser drawing, like Teeinblue. It covers the whole page; the floating phone preview hides while it is open.
  let instantPreview = false;
  let latestDrawing = null;
  let lightbox = null;
  let lightboxUrl = null;
  let galleryButton = null;
  let galleryHidden = false;
  // Studio AI photo flow: while it is open the designer frame covers the screen (progress card, then the Edit screen).
  let studioOpen = false;
  let studioRestore = [];
  // Floating preview frame (Macorner style): pinned at the top of phone screens while the customer edits, with close and zoom.
  let floatFrame = null;
  let floatStage = null;
  let floatZoom = 1;
  let floatPan = { x: 0, y: 0 };
  let floatDismissed = false;
  let floatDismissedAt = 0;
  let designerRefs = null;

  const mobile = window.matchMedia(MOBILE_QUERY);

  function createRequestId() {
    if (window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }
    return `kag-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  function setExternalPreview(enabled) {
    if (!sourceFrame?.contentWindow) {
      externalPreviewEnabled = false;
      return;
    }
    if (externalPreviewEnabled === enabled) {
      return;
    }
    externalPreviewEnabled = enabled;
    sourceFrame.contentWindow.postMessage(
      { type: EXTERNAL_PREVIEW_MESSAGE, enabled },
      DESIGNER_ORIGIN
    );
  }

  function injectStyles() {
    if (document.getElementById("koolezt-pag-preview-popup-styles")) {
      return;
    }

    const style = document.createElement("style");
    style.id = "koolezt-pag-preview-popup-styles";
    style.textContent = `
      .koolezt-pag-lightbox { position: fixed; inset: 0; z-index: 2147483646; display: flex; align-items: center; justify-content: center; padding: 16px; box-sizing: border-box; background: rgba(0, 0, 0, .6); }
      .koolezt-pag-lightbox[hidden] { display: none; }
      .koolezt-pag-lightbox__box { display: flex; flex-direction: column; width: min(600px, 100%); max-height: calc(100dvh - 32px); padding: 24px 24px 12px; box-sizing: border-box; background: #fff; }
      .koolezt-pag-lightbox__image { display: block; width: 100%; min-height: 0; aspect-ratio: 1 / 1; object-fit: contain; flex: 1 1 auto; }
      .koolezt-pag-lightbox__title { margin: 0 0 4px; font-size: 20px; font-weight: 700; line-height: 1.3; color: #111; }
      .koolezt-pag-lightbox__note { margin: 0 0 12px; font-size: 15px; line-height: 1.4; color: #444; }
      .koolezt-pag-lightbox__actions { display: flex; gap: 10px; margin-top: 14px; }
      .koolezt-pag-lightbox__cart { flex: 1 1 auto; min-height: 48px; padding: 0 18px; border: 0; border-radius: 999px; background: #111; color: #fff; font: inherit; font-size: 16px; font-weight: 700; cursor: pointer; }
      .koolezt-pag-lightbox__close { flex: 0 0 auto; min-height: 48px; padding: 0 22px; border: 0; border-radius: 999px; background: #f2f2f2; color: #111; font: inherit; font-size: 16px; cursor: pointer; }
      .koolezt-pag-lightbox__cart:focus-visible, .koolezt-pag-lightbox__close:focus-visible { outline: 2px solid #111; outline-offset: 2px; }
      /* The mini cart (or any side drawer of the theme) is in front: the floating preview steps aside. */
      body.side_Drawer_open .koolezt-pag-float-frame, body.yv_side_Drawer_open .koolezt-pag-float-frame { display: none !important; }
      html.koolezt-pag-lightbox-open { overflow: hidden; }
      .koolezt-pag-designer__inline-preview { text-transform: uppercase; color: #02a1cf !important; border-color: #02a1cf !important; }
      html.koolezt-pag-to-cart [data-side-drawer], html.koolezt-pag-to-cart #mini__cart, html.koolezt-pag-to-cart .wrapper-overlay { display: none !important; }
      html.koolezt-pag-lightbox-open .koolezt-pag-float-frame { display: none !important; }
      .koolezt-pag-preview-popup,
      .koolezt-pag-preview-restore {
        display: none;
      }

      @media (max-width: 749px) {
        .koolezt-pag-preview-popup {
          position: fixed;
          z-index: 90;
          top: max(8px, env(safe-area-inset-top));
          left: 50%;
          width: min(54vw, 224px);
          aspect-ratio: 1 / 1;
          box-sizing: border-box;
          transform: translateX(-50%);
          overflow: hidden;
          border: 1px solid rgba(23, 54, 64, 0.16);
          border-radius: 16px;
          background: #fff;
          box-shadow: 0 10px 34px rgba(23, 54, 64, 0.24);
        }

        .koolezt-pag-preview-popup[data-visible="true"] {
          display: block;
        }

        .koolezt-pag-preview-popup__frame {
          display: block;
          width: 100%;
          height: 100%;
          border: 0;
          background: #fff;
        }

        .koolezt-pag-preview-popup__close,
        .koolezt-pag-preview-restore {
          appearance: none;
          position: fixed;
          z-index: 91;
          display: grid;
          place-items: center;
          width: 42px;
          height: 42px;
          min-width: 42px;
          min-height: 42px;
          margin: 0;
          padding: 0;
          border: 1px solid rgba(23, 54, 64, 0.18);
          border-radius: 999px;
          background: #fff;
          color: #173640;
          box-shadow: 0 6px 20px rgba(23, 54, 64, 0.22);
          font: 700 25px/1 system-ui, sans-serif;
          cursor: pointer;
        }

        .koolezt-pag-preview-popup__close {
          position: absolute;
          top: 7px;
          right: 7px;
          z-index: 2;
          width: 36px;
          height: 36px;
          min-width: 36px;
          min-height: 36px;
          font-size: 22px;
        }

        .koolezt-pag-preview-restore {
          top: max(10px, env(safe-area-inset-top));
          right: 12px;
          color: #fff;
          background: #02a1cf;
          border-color: #02a1cf;
        }

        .koolezt-pag-preview-restore[data-visible="true"] {
          display: grid;
        }

        .koolezt-pag-preview-popup__close:focus-visible,
        .koolezt-pag-preview-restore:focus-visible {
          outline: 3px solid #173640;
          outline-offset: 2px;
        }

        .koolezt-pag-gallery-preview {
          appearance: none;
          position: fixed;
          z-index: 90;
          left: 50%;
          bottom: max(16px, env(safe-area-inset-bottom));
          transform: translateX(-50%);
          margin: 0;
          padding: 12px 26px;
          border: 0;
          border-radius: 999px;
          background: #02a1cf;
          color: #fff;
          box-shadow: 0 6px 20px rgba(23, 54, 64, 0.28);
          font: 700 15px/1 system-ui, sans-serif;
          cursor: pointer;
        }

        .koolezt-pag-gallery-preview[data-visible="true"] {
          display: block;
        }

        .koolezt-pag-gallery-design__photos:focus-visible,
        .koolezt-pag-gallery-preview:focus-visible {
          outline: 3px solid #173640;
          outline-offset: 2px;
        }
      }

      .koolezt-pag-gallery-design,
      .koolezt-pag-gallery-preview,
      .koolezt-pag-float-frame {
        display: none;
      }

      /* Product gallery mode, desktop and phone: the design takes the place of the main product image. */
        .koolezt-pag-gallery-design[data-visible="true"] {
        position: absolute;
        z-index: 5;
        inset: 0;
        display: block;
        background: #fff;
      }

      .koolezt-pag-float-frame__image {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: contain;
        background: #fff;
        transform-origin: center;
      }

      .koolezt-pag-gallery-design__image {
        user-select: none;
        -webkit-user-select: none;
        -webkit-user-drag: none;
        position: absolute;
        inset: 0;
        display: block;
        width: 100%;
        height: 100%;
        object-fit: contain;
        background: #fff;
      }

      .koolezt-pag-gallery-design .koolezt-pag-preview-popup__frame {
        position: absolute;
        inset: 0;
        display: block;
        width: 100%;
        height: 100%;
        border: 0;
        background: #fff;
      }

      .koolezt-pag-gallery-design__photos {
        appearance: none;
        position: absolute;
        z-index: 2;
        right: 10px;
        bottom: 10px;
        margin: 0;
        padding: 8px 14px;
        border: 1px solid rgba(23, 54, 64, 0.18);
        border-radius: 999px;
        background: #fff;
        color: #173640;
        box-shadow: 0 4px 14px rgba(23, 54, 64, 0.18);
        font: 600 13px/1 system-ui, sans-serif;
        cursor: pointer;
      }


      /* No success text under the designer (per the user); warnings and errors from the cart button still show. */
      .koolezt-pag-designer__status:not(.koolezt-pag-designer__status--error) {
        display: none !important;
      }

      /* Nor the red "Please wait for your new preview" note while a change renders (per the user): the design is
         already drawn on the page. Add to Cart stays blocked until the print is ready and says so if pressed. */
      .koolezt-pag-designer__status[data-koolezt-quiet="true"] {
        display: none !important;
      }

      @media (max-width: 749px) {
        .koolezt-pag-float-frame[data-visible="true"] {
          position: fixed;
          z-index: 90;
          top: 0;
          left: 0;
          right: 0;
          display: block;
          height: min(31.5vh, 270px); /* 3/4 of the earlier min(42vh, 360px), per the user: room to customize below */
          padding-top: env(safe-area-inset-top);
          border-radius: 0 0 18px 18px;
          overflow: hidden;
          background: #fff;
          box-shadow: 0 6px 22px rgba(23, 54, 64, 0.2);
        }

        .koolezt-pag-float-frame__stage {
          position: absolute;
          inset: env(safe-area-inset-top) 0 0 0;
          overflow: hidden;
          touch-action: none;
        }

        .koolezt-pag-float-frame__stage .koolezt-pag-preview-popup__frame {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          border: 0;
          transform-origin: center;
          transition: transform 0.15s ease;
          pointer-events: none;
        }

        .koolezt-pag-float-frame__button {
          appearance: none;
          position: absolute;
          right: 12px;
          z-index: 2;
          display: grid;
          place-items: center;
          width: 44px;
          height: 44px;
          margin: 0;
          padding: 0;
          border: 0;
          border-radius: 50%;
          background: #fff;
          color: #173640;
          box-shadow: 0 2px 10px rgba(23, 54, 64, 0.22);
          cursor: pointer;
        }

        .koolezt-pag-float-frame__button:focus-visible {
          outline: 3px solid #02a1cf;
          outline-offset: 2px;
        }
      }

      iframe.koolezt-pag-studio-frame {
        position: fixed !important;
        inset: 0 !important;
        z-index: 2147483600 !important;
        width: 100vw !important;
        height: 100vh !important;
        height: 100dvh !important;
        max-width: none !important;
        max-height: none !important;
        margin: 0 !important;
        border: 0 !important;
        background: transparent !important;
        color-scheme: light;
      }

      html.koolezt-pag-studio-open,
      html.koolezt-pag-studio-open body {
        overflow: hidden !important;
      }
    `;
    document.head.append(style);
  }

  function buildPopup() {
    if (popup) {
      return;
    }

    injectStyles();

    popup = document.createElement("aside");
    popup.className = "koolezt-pag-preview-popup";
    popup.setAttribute("aria-label", "Live personalized design preview");
    popup.dataset.visible = "false";

    const closeButton = document.createElement("button");
    closeButton.type = "button";
    closeButton.className = "koolezt-pag-preview-popup__close";
    closeButton.setAttribute("aria-label", "Hide live design preview");
    closeButton.textContent = "×";
    closeButton.addEventListener("click", () => {
      dismissed = true;
      updateVisibility();
      restoreButton?.focus({ preventScroll: true });
    });
    popup.append(closeButton);

    restoreButton = document.createElement("button");
    restoreButton.type = "button";
    restoreButton.className = "koolezt-pag-preview-restore";
    restoreButton.setAttribute("aria-label", "Show live design preview");
    restoreButton.textContent = "+";
    restoreButton.dataset.visible = "false";
    restoreButton.addEventListener("click", () => {
      dismissed = false;
      updateVisibility();
      popup?.querySelector(".koolezt-pag-preview-popup__close")?.focus({
        preventScroll: true
      });
    });

    document.body.append(popup);
  }

  function removePreviewFrame() {
    previewFrame?.remove();
    previewFrame = null;
    renderReady = false;
    requestId = "";
    frameRequestedFor = "";
  }

  function previewUrl() {
    if (!sourceFrame?.src) {
      return null;
    }

    try {
      const url = new URL(sourceFrame.src);
      if (url.origin !== DESIGNER_ORIGIN) {
        return null;
      }
      requestId = createRequestId();
      url.searchParams.set("preview", "1");
      url.searchParams.set("preview_request", requestId);
      url.hash = "";
      return url;
    } catch {
      return null;
    }
  }

  function findGallery() {
    if (gallery?.isConnected && galleryForMobile === mobile.matches) {
      return gallery;
    }
    galleryForMobile = mobile.matches;
    // Desktop: only the main image, so the thumbnails stay usable (Teeinblue shows the design as the main image).
    // The design is the gallery's first slide (like Teeinblue): arrows and thumbnails move between it and the mockups.
    const firstSlide = ".product-detail-slider .yv-product-big-slider .yv-product-slider-item.big__slider";
    for (const selector of mobile.matches ? [firstSlide, ...GALLERY_SELECTORS] : [firstSlide, ".product-detail-slider .yv-product-big-slider", ...GALLERY_SELECTORS]) {
      const element = document.querySelector(selector);
      if (element && element.getBoundingClientRect().height > 0) {
        gallery = element;
        return gallery;
      }
    }
    gallery = null;
    return null;
  }

  function buildGallery() {
    const target = findGallery();
    if (!target) {
      return null;
    }
    if (galleryOverlay?.parentElement === target) {
      return galleryOverlay;
    }

    galleryOverlay?.remove();
    if (window.getComputedStyle(target).position === "static") {
      target.style.position = "relative";
    }
    galleryOverlay = document.createElement("div");
    galleryOverlay.className = "koolezt-pag-gallery-design";
    galleryOverlay.setAttribute("aria-label", "Your personalized design");
    galleryOverlay.dataset.visible = "false";

    const photosButton = document.createElement("button");
    photosButton.type = "button";
    photosButton.className = "koolezt-pag-gallery-design__photos";
    photosButton.textContent = "Product photos";
    photosButton.addEventListener("click", () => {
      // In a slider, go to the next slide (the first mockup); elsewhere hide the design.
      const next = target.closest(".yv-product-big-slider, .product-detail-slider")?.querySelector(".flickity-button.next, .flickity-prev-next-button.next");
      if (next) { next.click(); return; }
      galleryHidden = true;
      updateVisibility();
    });
    galleryOverlay.append(photosButton);
    target.append(galleryOverlay);

    return galleryOverlay;
  }

  function applyFloatZoom() {
    const frameNode = floatStage?.querySelector(".koolezt-pag-preview-popup__frame, .koolezt-pag-float-frame__image");
    if (!frameNode) return;
    const limit = (floatZoom - 1) * 50;
    floatPan.x = Math.max(-limit, Math.min(limit, floatPan.x));
    floatPan.y = Math.max(-limit, Math.min(limit, floatPan.y));
    frameNode.style.transform = floatZoom > 1 ? `translate(${floatPan.x}%, ${floatPan.y}%) scale(${floatZoom})` : "";
  }

  function buildFloatFrame() {
    if (floatFrame) return floatStage;
    floatFrame = document.createElement("aside");
    floatFrame.className = "koolezt-pag-float-frame";
    floatFrame.setAttribute("aria-label", "Your design preview");
    floatFrame.dataset.visible = "false";
    floatStage = document.createElement("div");
    floatStage.className = "koolezt-pag-float-frame__stage";
    const icon = path => `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true">${path}</svg>`;
    const button = (label, top, path, onClick) => { const node = document.createElement("button"); node.type = "button"; node.className = "koolezt-pag-float-frame__button"; node.style.top = top; node.setAttribute("aria-label", label); node.innerHTML = icon(path); node.addEventListener("click", onClick); return node; };
    const close = button("Hide preview", "12px", '<path d="M6 6l12 12M18 6L6 18"/>', () => { floatDismissed = true; floatDismissedAt = window.scrollY; updateVisibility(); });
    const zoomIn = button("Zoom in", "calc(100% - 116px)", '<circle cx="11" cy="11" r="7"/><path d="M11 8v6M8 11h6M20 20l-4-4"/>', () => { floatZoom = Math.min(3, floatZoom + 0.5); applyFloatZoom(); });
    const zoomOut = button("Zoom out", "calc(100% - 62px)", '<circle cx="11" cy="11" r="7"/><path d="M8 11h6M20 20l-4-4"/>', () => { floatZoom = Math.max(1, floatZoom - 0.5); if (floatZoom === 1) floatPan = { x: 0, y: 0 }; applyFloatZoom(); });
    // Zoomed in, the customer can drag to look around the design.
    let drag = null;
    floatStage.addEventListener("pointerdown", event => { if (floatZoom <= 1) return; drag = { x: event.clientX, y: event.clientY, pan: { ...floatPan } }; floatStage.setPointerCapture(event.pointerId); });
    floatStage.addEventListener("pointermove", event => { if (!drag) return; const box = floatStage.getBoundingClientRect(); floatPan = { x: drag.pan.x + (event.clientX - drag.x) / box.width * 100, y: drag.pan.y + (event.clientY - drag.y) / box.height * 100 }; applyFloatZoom(); });
    const endDrag = () => { drag = null; };
    floatStage.addEventListener("pointerup", endDrag);
    floatStage.addEventListener("pointercancel", endDrag);
    floatFrame.append(floatStage, close, zoomIn, zoomOut);
    document.body.append(floatFrame);
    return floatStage;
  }

  // The product form's Add to Cart button (the frame stays until it scrolls up past the frame, like Macorner).
  function cartButton() {
    // Themes differ: take the visible Add to Cart button that comes first below the designer.
    // When Add to Cart is hidden (the Preview window has its own), the Preview button marks the end instead.
    const candidates = [...document.querySelectorAll('form[action*="/cart/add"] [type="submit"], form[action*="/cart/add"] [name="add"], .add_to_cart, .koolezt-pag-designer__inline-preview, button')]
      .filter(node => node.offsetParent !== null && node.getBoundingClientRect().height > 0 && (node.matches('form[action*="/cart/add"] [type="submit"], [name="add"], .add_to_cart, .koolezt-pag-designer__inline-preview') || /add to cart/i.test(node.textContent)));
    const designerTop = sourceFrame ? sourceFrame.getBoundingClientRect().top : -Infinity;
    return candidates.filter(node => node.getBoundingClientRect().top >= designerTop).sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top)[0] ?? null;
  }

  // The frame shows from the designer down to Add to Cart, once the product photos are (almost) out of view.
  let floatFrameHeight = 0;
  window.addEventListener("resize", () => { floatFrameHeight = 0; }, { passive: true });
  function floatFrameZone() {
    if (!(["floating", "button"].includes(phonePreview) || (phonePreview === "gallery" && galleryImageUrl)) || !mobile.matches || !previewReady || !renderReady || studioOpen || !sourceFrame) return false;
    const designerBox = sourceFrame.getBoundingClientRect(), galleryBox = findGallery()?.getBoundingClientRect(), cartBox = cartButton()?.getBoundingClientRect();
    // The frame's height whether it is shown or not (the same size as Teeinblue's floating preview), so showing it does
    // not change the point where it shows.
    if (floatFrame?.offsetHeight) floatFrameHeight = floatFrame.offsetHeight;
    const frameBottom = floatFrameHeight || Math.round(Math.min(window.innerHeight * 0.315, 270));
    // Like Teeinblue's floating preview: as soon as the gallery's bottom edge passes under the frame, the frame takes
    // the gallery's place, so the price and the size choice below it are never covered when it appears.
    const galleryGone = !galleryBox || galleryBox.bottom < frameBottom;
    const endBottom = cartBox && cartBox.height > 0 ? cartBox.bottom : designerBox.bottom;
    return galleryGone && endBottom > frameBottom;
  }

  // × hides the frame for now; it comes back once the customer scrolls on, up or down (about 120px).
  function floatFrameWanted() {
    if (floatDismissed && Math.abs(window.scrollY - floatDismissedAt) > 120) floatDismissed = false;
    return floatFrameZone() && !floatDismissed;
  }

  function cartButtonsOnScreen() {
    const form = designer?.closest("form") ?? document.querySelector('form[action*="/cart/add"]');
    const buttons = form ? [...form.querySelectorAll('button[type="submit"], [name="add"], [data-koolezt-pag-designer-action="preview"]'), ...[...form.querySelectorAll("button")].filter(button => /add to cart/i.test(button.textContent))] : [];
    for (const button of buttons) {
      const box = button.getBoundingClientRect();
      if (box.height > 0 && box.bottom > 0 && box.top < window.innerHeight) return true;
    }
    return false;
  }

  function openLightbox() {
    if (!latestDrawing) return false;
    if (!lightbox) {
      lightbox = document.createElement("div");
      lightbox.className = "koolezt-pag-lightbox";
      lightbox.setAttribute("role", "dialog");
      lightbox.setAttribute("aria-modal", "true");
      lightbox.setAttribute("aria-label", "Your design preview");
      lightbox.innerHTML = '<div class="koolezt-pag-lightbox__box"><p class="koolezt-pag-lightbox__title">Looks good to go?</p><p class="koolezt-pag-lightbox__note">Please double-check your design before adding it to your cart.</p><img class="koolezt-pag-lightbox__image" alt="Your personalized design"><div class="koolezt-pag-lightbox__actions"><button type="button" class="koolezt-pag-lightbox__cart">Add to cart</button><button type="button" class="koolezt-pag-lightbox__close">Close</button></div></div>';
      lightbox.addEventListener("click", event => {
        if (event.target.closest(".koolezt-pag-lightbox__cart")) { addToCartFromLightbox(); return; }
        if (event.target === lightbox || event.target.closest(".koolezt-pag-lightbox__close")) closeLightbox();
      });
      document.body.append(lightbox);
    }
    showLightboxDrawing();
    styleLightboxCart();
    lightbox.hidden = false;
    document.documentElement.classList.add("koolezt-pag-lightbox-open");
    lightbox.querySelector(".koolezt-pag-lightbox__close").focus({ preventScroll: true });
    return true;
  }

  // The product page's own Add to Cart button: the window's button looks like it and presses it, so the usual checks
  // (required answers, the design made for the order) run as they do from the page.
  function pageCartButton() {
    const form = designer?.closest("form") ?? document.querySelector('form[action*="/cart/add"]');
    const buttons = form ? [...form.querySelectorAll('button[type="submit"], [name="add"]')] : [];
    return buttons.find(button => button.getBoundingClientRect().height > 0) || buttons[0] || null;
  }

  // The page's Add to Cart button is hidden while the action box floats (a <style> next to this script's tag in
  // layout/theme.liquid): every design adds from the Preview window, AI or not (per the user, 2026-10-26). It comes
  // back only if the designer has not answered after 10 seconds.
  let designerKnown = false;
  window.setTimeout(() => { if (!designerKnown) document.documentElement.classList.add("koolezt-pag-page-cart"); }, 10000);
  function syncPageCart() {
    document.documentElement.classList.toggle("koolezt-pag-page-cart", !designerKnown);
  }

  function styleLightboxCart() {
    const source = pageCartButton(), target = lightbox?.querySelector(".koolezt-pag-lightbox__cart");
    if (!source || !target) return;
    const style = getComputedStyle(source);
    if (style.backgroundColor && style.backgroundColor !== "rgba(0, 0, 0, 0)") target.style.background = style.backgroundColor;
    if (style.color) target.style.color = style.color;
    if (style.borderRadius) target.style.borderRadius = style.borderRadius;
  }

  function addToCartFromLightbox() {
    const button = pageCartButton();
    closeLightbox();
    button?.click();
  }

  function showLightboxDrawing() {
    if (!lightbox || !latestDrawing) return;
    const url = URL.createObjectURL(latestDrawing);
    lightbox.querySelector(".koolezt-pag-lightbox__image").src = url;
    if (lightboxUrl) URL.revokeObjectURL(lightboxUrl);
    lightboxUrl = url;
  }

  function closeLightbox() {
    if (!lightbox || lightbox.hidden) return;
    lightbox.hidden = true;
    document.documentElement.classList.remove("koolezt-pag-lightbox-open");
    designer?.querySelector('[data-koolezt-pag-designer-action="preview"]')?.focus?.({ preventScroll: true });
  }

  // The Preview button (Store Tools) opens the page-level window whenever the designer has drawn the design, AI or
  // not; while an AI photo is still being made there is no drawing and the designer's own preview answers instead.
  window.addEventListener("click", event => {
    const action = event.target?.closest?.('[data-koolezt-pag-designer-action="preview"]');
    if (!action || !designer || !latestDrawing) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    openLightbox();
  }, true);
  window.addEventListener("keydown", event => { if (event.key === "Escape") closeLightbox(); });

  // Store Tools keeps Preview greyed out until the design is finished (every required answer given). Per the user
  // (2026-10-26) Preview works as soon as the designer has drawn something; Add to cart in the window still runs the
  // page's checks, which ask for any missing answer.
  function enablePreviewWithDrawing() {
    if (!latestDrawing) return;
    document.querySelectorAll('[data-koolezt-pag-designer-action="preview"]').forEach(button => {
      if (!button.disabled && button.getAttribute("aria-disabled") !== "true") return;
      button.disabled = false;
      button.setAttribute("aria-disabled", "false");
      button.title = "Preview your design";
    });
  }
  new MutationObserver(enablePreviewWithDrawing).observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ["disabled", "aria-disabled"] });

  // Safari shows an <img> blank between a new src and its decode: a white flash in the gallery and the floating preview on
  // each style or pet-count change, mostly on a first visit (user's recording, 2026-10-30). While a design is already
  // shown, the new drawing is decoded off screen first and then swapped in, so the old one stays until then.
  let gallerySwap = 0;
  function showGalleryImage(blob) {
    if (!galleryImageUrl) return placeGalleryImage(URL.createObjectURL(blob));
    if (!buildGallery()) return false;
    const swap = ++gallerySwap, url = URL.createObjectURL(blob), probe = new Image();
    probe.src = url;
    const place = () => { if (swap === gallerySwap) placeGalleryImage(url); else URL.revokeObjectURL(url); };
    (probe.decode ? probe.decode() : Promise.resolve()).then(place, place);
    return true;
  }

  function placeGalleryImage(url) {
    const overlay = buildGallery();
    if (!overlay) { URL.revokeObjectURL(url); return false; }
    let image = overlay.querySelector(".koolezt-pag-gallery-design__image");
    if (!image) {
      image = document.createElement("img");
      image.className = "koolezt-pag-gallery-design__image";
      image.alt = "Your personalized design";
      overlay.prepend(image);
    }
    image.src = url;
    showDesignThumb(url);
    // Phones: the same picture in the floating preview pinned at the top while the gallery is out of view.
    if (mobile.matches) {
      const stage = buildFloatFrame();
      stage.querySelector(".koolezt-pag-preview-popup__frame")?.remove();
      let floating = stage.querySelector(".koolezt-pag-float-frame__image");
      if (!floating) { floating = document.createElement("img"); floating.className = "koolezt-pag-float-frame__image"; floating.alt = "Your personalized design"; stage.append(floating); }
      floating.src = url;
      applyFloatZoom();
    }
    if (galleryImageUrl) URL.revokeObjectURL(galleryImageUrl);
    galleryImageUrl = url;
    return true;
  }

  const firstThumb = () => document.querySelector(".product-detail-slider .yv-product-small-slider .yv-product-slider-item img");
  function showDesignThumb(url) {
    const thumb = firstThumb();
    if (!thumb) return;
    if (!thumb.dataset.kagOriginalSrc) { thumb.dataset.kagOriginalSrc = thumb.getAttribute("src") || ""; thumb.dataset.kagOriginalSrcset = thumb.getAttribute("srcset") || ""; }
    thumb.removeAttribute("srcset");
    thumb.src = url;
  }
  function restoreThumb() {
    const thumb = firstThumb();
    if (!thumb?.dataset.kagOriginalSrc) return;
    thumb.src = thumb.dataset.kagOriginalSrc;
    if (thumb.dataset.kagOriginalSrcset) thumb.setAttribute("srcset", thumb.dataset.kagOriginalSrcset);
    delete thumb.dataset.kagOriginalSrc; delete thumb.dataset.kagOriginalSrcset;
  }

  function clearGalleryImage() {
    gallerySwap += 1;
    restoreThumb();
    floatStage?.querySelector(".koolezt-pag-float-frame__image")?.remove();
    galleryOverlay?.querySelector(".koolezt-pag-gallery-design__image")?.remove();
    if (galleryImageUrl) URL.revokeObjectURL(galleryImageUrl);
    galleryImageUrl = null;
  }

  function previewHost() {
    if (phonePreview === "gallery") {
      return buildGallery();
    }
    if (phonePreview === "floating" || phonePreview === "button") {
      return buildFloatFrame();
    }
    return phonePreview === "drawer" ? popup : null;
  }

  function ensurePreviewFrame() {
    if (!previewReady || (!mobile.matches && phonePreview !== "gallery") || !popup || !sourceFrame) {
      return;
    }
    if (phonePreview === "gallery" && (galleryImageUrl || instantPreview)) {
      // The gallery already shows the designer's own picture, or will as soon as a no-AI design draws it in the
      // browser: no preview frame (it would load the whole designer a second time while the first one starts).
      previewFrame?.remove();
      previewFrame = null;
      frameRequestedFor = "";
      return;
    }

    const host = previewHost();
    if (!host) {
      removePreviewFrame();
      return;
    }

    const source = sourceFrame.src;
    if (previewFrame && frameRequestedFor === source && previewFrame.parentElement === host) {
      return;
    }

    removePreviewFrame();
    const url = previewUrl();
    if (!url) {
      return;
    }

    previewFrame = document.createElement("iframe");
    previewFrame.className = "koolezt-pag-preview-popup__frame";
    previewFrame.title = "Live personalized design preview";
    previewFrame.src = url.toString();
    previewFrame.setAttribute("sandbox", "allow-scripts allow-same-origin");
    previewFrame.setAttribute(
      "allow",
      "camera 'none'; microphone 'none'; geolocation 'none'"
    );
    frameRequestedFor = source;
    host.append(previewFrame);
  }

  function shouldStick() {
    if (phonePreview !== "drawer" || !mobile.matches || !previewReady || !renderReady || !sourceFrame) {
      return false;
    }

    const rect = sourceFrame.getBoundingClientRect();
    const minimumRemaining = Math.min(window.innerHeight * 0.3, 240);
    return rect.top < 8 && rect.bottom > minimumRemaining;
  }

  function updateVisibility() {
    scheduled = false;
    buildPopup();
    ensurePreviewFrame();

    const sticky = shouldStick() && !studioOpen;
    const showPopup = sticky && !dismissed;
    popup.dataset.visible = String(showPopup);
    restoreButton.dataset.visible = String(sticky && dismissed);

    const galleryReady = phonePreview === "gallery" && previewReady && renderReady && Boolean(galleryOverlay);
    if (galleryOverlay) {
      galleryOverlay.dataset.visible = String(galleryReady && !galleryHidden);
    }
    if (galleryButton) {
      const galleryBox = galleryReady ? findGallery()?.getBoundingClientRect() : null;
      const galleryOffScreen = Boolean(galleryBox) && (galleryBox.bottom < 80 || galleryBox.top > window.innerHeight);
      // The product form's own buttons (Add to Cart, the theme's Preview) sit at the bottom of the designer; the
      // floating button would cover them, so it steps aside while they are on screen.
      const formButtons = galleryReady ? cartButtonsOnScreen() : false;
      galleryButton.dataset.visible = String(galleryReady && mobile.matches && !studioOpen && !formButtons && (galleryOffScreen || galleryHidden));
    }
    if (floatFrame) {
      floatFrame.dataset.visible = String(floatFrameWanted());
    }
    setExternalPreview(phonePreview !== "off" && (mobile.matches || phonePreview === "gallery") && previewReady);
  }

  function scheduleUpdate() {
    if (scheduled) {
      return;
    }
    scheduled = true;
    window.requestAnimationFrame(updateVisibility);
  }

  function resetPreview() {
    clearGalleryImage();
    previewReady = false;
    dismissed = false;
    floatDismissed = false;
    floatZoom = 1;
    floatPan = { x: 0, y: 0 };
    if (floatFrame) {
      floatFrame.dataset.visible = "false";
    }
    galleryHidden = false;
    removePreviewFrame();
    if (galleryOverlay) {
      galleryOverlay.dataset.visible = "false";
    }
    if (galleryButton) {
      galleryButton.dataset.visible = "false";
    }
    if (popup) {
      popup.dataset.visible = "false";
    }
    if (restoreButton) {
      restoreButton.dataset.visible = "false";
    }
    setExternalPreview(false);
  }

  function bindDesigner() {
    const nextDesigner = document.querySelector(DESIGNER_SELECTOR);
    const nextFrame = nextDesigner?.querySelector(FRAME_SELECTOR) || null;

    if (nextDesigner === designer && nextFrame === sourceFrame) {
      return;
    }

    setExternalPreview(false);
    closeStudio(false);
    designer = nextDesigner;
    sourceFrame = nextFrame;
    closeLightbox();
    instantPreview = false;
    latestDrawing = null;
    watchStatus();
    phonePreview = "drawer";
    resetPreview();
    scheduleUpdate();
  }

  const RENDER_WAIT_NOTE = "Please wait for your new preview before adding this item to cart.";
  const ADDED_NOTE = "Added to cart.";
  const CART_STEPS = ["Preparing your personalized item", "Personalized design ready", ADDED_NOTE];
  let goingToCart = false;
  let statusObserver = null;
  function watchStatus() {
    statusObserver?.disconnect();
    const node = designer?.querySelector(".koolezt-pag-designer__status");
    if (!node) return;
    const quiet = () => {
      const text = node.textContent.trim();
      node.dataset.kooleztQuiet = String(text === RENDER_WAIT_NOTE);
      // After Add to Cart, personalized items go to the cart page (like Macorner and TrendingCustom), where the customer
      // checks the design. Store Tools says "Added to cart." only once the design is locked to the cart item, so the
      // page is left after that step, never during it.
      // From "Preparing…" until the cart page opens, the theme's mini cart (which Store Tools opens after adding) stays
      // hidden, so the customer goes straight from the product to the cart page. An error message shows it again.
      if (CART_STEPS.some(step => text.startsWith(step))) document.documentElement.classList.add("koolezt-pag-to-cart");
      else if (/^We couldn|^Please /.test(text)) document.documentElement.classList.remove("koolezt-pag-to-cart");
      if (!goingToCart && text.startsWith(ADDED_NOTE)) {
        goingToCart = true;
        window.location.assign(`${window.Shopify?.routes?.root || "/"}cart`);
      }
    };
    statusObserver = new MutationObserver(quiet);
    statusObserver.observe(node, { childList: true, characterData: true, subtree: true });
    quiet();
  }

  function tellStudioHost() {
    if (sourceFrame?.contentWindow) {
      sourceFrame.contentWindow.postMessage({ type: STUDIO_HOST_MESSAGE, version: 1 }, DESIGNER_ORIGIN);
    }
  }

  // Themes sometimes put transforms or stacking contexts on product sections; those would keep a fixed frame from
  // covering the screen, so they are switched off while the studio is open and put back afterwards.
  function liftAncestors() {
    for (let element = sourceFrame?.parentElement; element && element !== document.body && element !== document.documentElement; element = element.parentElement) {
      const style = window.getComputedStyle(element);
      const changes = {};
      for (const property of ["transform", "filter", "perspective", "backdropFilter"]) {
        if (style[property] && style[property] !== "none") changes[property] = "none";
      }
      if (style.contain && style.contain !== "none") changes.contain = "none";
      if (style.willChange && style.willChange !== "auto") changes.willChange = "auto";
      // Sticky and other positioned boxes keep the frame at their own layer; lift them above the rest of the page.
      if (style.position !== "static" || style.zIndex !== "auto") changes.zIndex = "2147483600";
      if (Object.keys(changes).length) {
        studioRestore.push([element, Object.fromEntries(Object.keys(changes).map(property => [property, element.style[property]]))]);
        Object.assign(element.style, changes);
      }
    }
  }

  function openStudio() {
    if (studioOpen || !sourceFrame) return;
    studioOpen = true;
    injectStyles();
    const holder = sourceFrame.parentElement;
    if (holder) {
      studioRestore.push([holder, { minHeight: holder.style.minHeight }]);
      holder.style.minHeight = `${Math.round(sourceFrame.getBoundingClientRect().height)}px`;
    }
    liftAncestors();
    document.documentElement.classList.add("koolezt-pag-studio-open");
    sourceFrame.classList.add("koolezt-pag-studio-frame");
    scheduleUpdate();
  }

  function closeStudio(done) {
    if (!studioOpen) return;
    studioOpen = false;
    sourceFrame?.classList.remove("koolezt-pag-studio-frame");
    document.documentElement.classList.remove("koolezt-pag-studio-open");
    for (const [element, values] of studioRestore.reverse()) Object.assign(element.style, values);
    studioRestore = [];
    if (done && phonePreview === "gallery" && mobile.matches) {
      galleryHidden = false;
      updateVisibility();
      findGallery()?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    scheduleUpdate();
  }

  function receiveMessage(event) {
    if (event.origin !== DESIGNER_ORIGIN) {
      return;
    }

    if (event.source === sourceFrame?.contentWindow) {
      const mode = event.data?.type === PHONE_PREVIEW_MESSAGE ? event.data.mode : event.data?.phonePreview;
      if (event.data?.type === PHONE_PREVIEW_MESSAGE) {
        // The designer says from the start whether it is a no-AI design, so an AI design gets the page's Add to Cart back at once.
        if (typeof event.data.instant === "boolean") { instantPreview = event.data.instant; designerKnown = true; syncPageCart(); }
        tellStudioHost();
      }
      if (event.data?.type === STUDIO_MESSAGE && typeof event.data.open === "boolean") {
        if (event.data.open) openStudio();
        else closeStudio(event.data.done === true);
        return;
      }
      if (typeof event.data?.productReference === "string") {
        designerRefs = { productReference: event.data.productReference, variantReference: event.data.variantReference ?? null };
      }
      if (["drawer", "gallery", "floating", "button", "off"].includes(mode) && mode !== phonePreview) {
        phonePreview = mode;
        removePreviewFrame();
        ensurePreviewFrame();
        scheduleUpdate();
      }
      if (event.data?.type === LOCAL_PREVIEW_MESSAGE && event.data.image instanceof Blob) {
        latestDrawing = event.data.image;
        showLightboxDrawing();
        enablePreviewWithDrawing();
      }
      if (event.data?.type === READY_MESSAGE && typeof event.data.instant === "boolean") { instantPreview = event.data.instant; designerKnown = true; syncPageCart(); }
      if (event.data?.type === READY_MESSAGE) {
        // A new render (for example after Recustomize or the studio Edit screen) needs a fresh preview frame, unless
        // the frame is already open and the designer says it updates itself (liveFrame): it then loads the finished
        // preview without reloading.
        const keep = event.data.liveFrame === true && previewReady && previewFrame?.isConnected && frameRequestedFor === sourceFrame?.src;
        if (!keep && !(phonePreview === "gallery" && galleryImageUrl)) removePreviewFrame();
        previewReady = true;
        dismissed = false;
        floatDismissed = false;
        ensurePreviewFrame();
        scheduleUpdate();
      } else if (event.data?.type === INVALIDATED_MESSAGE) {
        // A change the designer draws in the browser (a layout, a breed, a size) keeps what is shown until the new drawing
        // replaces it; anything else (a new photo) resets it.
        if (!(event.data.localPreview === true && previewReady && (previewFrame?.isConnected || galleryImageUrl))) resetPreview();
      } else if (event.data?.type === LOCAL_PREVIEW_MESSAGE && phonePreview === "gallery" && event.data.image instanceof Blob && showGalleryImage(event.data.image)) {
        previewFrame?.remove();
        previewFrame = null;
        frameRequestedFor = "";
        previewReady = true;
        renderReady = true;
        galleryHidden = false;
        scheduleUpdate();
      } else if (event.data?.type === LOCAL_PREVIEW_MESSAGE && !previewReady) {
        previewReady = true;
        dismissed = false;
        floatDismissed = false;
        ensurePreviewFrame();
        scheduleUpdate();
      }
      return;
    }

    if (
      event.source === previewFrame?.contentWindow &&
      event.data?.type === RENDER_READY_MESSAGE &&
      event.data?.requestId === requestId
    ) {
      renderReady = true;
      scheduleUpdate();
    }
  }

  window.addEventListener("message", receiveMessage);
  window.addEventListener("scroll", scheduleUpdate, { passive: true });
  window.addEventListener("resize", scheduleUpdate);
  mobile.addEventListener?.("change", scheduleUpdate);

  const observer = new MutationObserver(() => {
    bindDesigner();
  });

  // Add-on products chosen with a designer toggle (for example "Shipping Protection (+$14.99)"). The designer reports
  // its toggle answers with pag-design-finalized; when Store Tools then adds the design to the cart, the add-on
  // product is added right after it, before the cart drawer opens. Only toggles whose product exists on this store
  // take part, so stores without the add-on product are unaffected. Each toggle's product is set in the Template (Add-on
  // product); designs from before that setting keep the old fixed pairing for Shipping Protection.
  const ADDON_PRODUCTS = { shipping_protection: "kag-shipping-protection" };
  const ADDON_HANDLE = /^[a-z0-9][a-z0-9-]{0,99}$/;
  const addonVariants = new Map();
  let pendingAddons = [];

  async function addonVariant(handle) {
    if (!addonVariants.has(handle)) {
      addonVariants.set(handle, fetch(new URL(`/products/${handle}.js`, window.location.origin), { credentials: "same-origin", headers: { Accept: "application/json" } })
        .then(response => response.ok ? response.json() : null)
        .then(product => product?.variants?.find(variant => variant.available)?.id ?? null)
        .catch(() => null));
    }
    return addonVariants.get(handle);
  }

  function rememberAddons(event) {
    if (event.origin !== DESIGNER_ORIGIN || event.data?.type !== "pag-design-finalized") return;
    const toggles = event.data.toggles && typeof event.data.toggles === "object" ? event.data.toggles : {};
    const chosen = Array.isArray(event.data.addons) ? event.data.addons.filter(handle => typeof handle === "string" && ADDON_HANDLE.test(handle)).slice(0, 10) : null;
    pendingAddons = chosen ?? Object.keys(ADDON_PRODUCTS).filter(key => toggles[key] === "on").map(key => ADDON_PRODUCTS[key]);
    pendingAddons.forEach(addonVariant);
    setTemplateProperty(event.data.template);
    setAddonsProperty(pendingAddons);
  }

  // The sign's chosen add-ons go on its cart line (hidden "_pag_addons", "none" when nothing is ticked), so the cart's
  // "Edit design" → "Update item" can add newly ticked add-ons and remove unticked ones.
  function setAddonsProperty(handles) {
    const frame = document.querySelector(`${DESIGNER_SELECTOR} ${FRAME_SELECTOR}`), root = frame?.closest("product-info, [data-product-root], section");
    const forms = root ? [...root.querySelectorAll('form[action*="/cart/add"]')] : [];
    for (const form of forms) {
      form.querySelectorAll("input[data-kag-addons-property]").forEach(input => input.remove());
      const input = document.createElement("input");
      input.type = "hidden"; input.name = "properties[_pag_addons]"; input.value = handles.length ? handles.join(",") : "none";
      input.dataset.kagAddonsProperty = "true";
      form.append(input);
    }
  }

  // "Show selected template on cart/checkout": the chosen template goes on the cart line as "<label>: <template>".
  function setTemplateProperty(template) {
    const frame = document.querySelector(`${DESIGNER_SELECTOR} ${FRAME_SELECTOR}`), root = frame?.closest("product-info, [data-product-root], section");
    const forms = root ? [...root.querySelectorAll('form[action*="/cart/add"]')] : [];
    for (const form of forms) {
      form.querySelectorAll("input[data-kag-template-property]").forEach(input => input.remove());
      const label = typeof template?.label === "string" ? template.label.trim().slice(0, 100) : "", value = typeof template?.value === "string" ? template.value.trim().slice(0, 100) : "";
      if (!label || !value || label.startsWith("_")) continue;
      const input = document.createElement("input");
      input.type = "hidden"; input.name = `properties[${label}]`; input.value = value; input.dataset.kagTemplateProperty = "";
      form.append(input);
    }
  }

  // Add-ons are charged per use (per the user, 2026-10-26): each listed add-on once per sign, times the sign's quantity,
  // so two dogs with a custom photo add two, and a second sign adds its own. Shipping protection stays once per cart.
  const ONCE_PER_CART = /shipping-protection/;
  async function addAddons(handles, signQuantity = 1) {
    let cart = null;
    try { cart = await nativeFetch(new URL("/cart.js", window.location.origin), { credentials: "same-origin", headers: { Accept: "application/json" } }).then(response => response.json()); } catch {}
    const counts = new Map();
    handles.forEach(handle => counts.set(handle, (counts.get(handle) || 0) + 1));
    for (const [handle, count] of counts) {
      const id = await addonVariant(handle), once = ONCE_PER_CART.test(handle);
      if (!id || (once && cart?.items?.some(item => item.variant_id === id))) continue;
      try {
        await nativeFetch(new URL("/cart/add.js", window.location.origin), { method: "POST", credentials: "same-origin", headers: { Accept: "application/json", "Content-Type": "application/json" }, body: JSON.stringify({ items: [{ id, quantity: once ? 1 : count * signQuantity }] }) });
      } catch {}
    }
  }

  const nativeFetch = window.fetch.bind(window);
  window.fetch = async function (resource, options) {
    const response = await nativeFetch(resource, options);
    try {
      const url = new URL(typeof resource === "string" ? resource : resource?.url ?? String(resource), window.location.origin);
      const body = options?.body;
      if (pendingAddons.length && response.ok && url.origin === window.location.origin && url.pathname.endsWith("/cart/add.js") && body instanceof FormData && body.get("properties[_pag_design_id]")) {
        const handles = pendingAddons, quantity = Math.max(1, Math.min(99, Number.parseInt(body.get("quantity"), 10) || 1));
        pendingAddons = [];
        await addAddons(handles, quantity);
      }
    } catch {}
    return response;
  };
  window.addEventListener("message", rememberAddons);

  function start() {
    buildPopup();
    bindDesigner();
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
})();

// Phones: the action box (Preview + Add to cart) floats at the bottom of the screen while the options are on screen.
// While it floats it shows Preview only (the Preview window has its own Add to cart); in its own place under the
// options it shows both. html.koolezt-actions-floating marks the floating state (the CSS hides Add to cart then).
(() => {
  "use strict";
  const PREVIEW = ".koolezt-pag-designer__inline-preview", FLOATING = "koolezt-actions-floating";
  let full = 0, line = null, queued = false;
  const update = () => {
    queued = false;
    const html = document.documentElement, preview = document.querySelector(PREVIEW);
    // KAG: Store Tools lifts Preview and Add to cart into a bar fixed at the bottom while the designer is on screen.
    const host = preview?.closest(".koolezt-pag-designer__action-host");
    if (host && getComputedStyle(host).position === "fixed") { html.classList.add(FLOATING); return; }
    const box = preview?.closest(".pdp-action-container");
    if (!box || getComputedStyle(box).position !== "sticky") { html.classList.remove(FLOATING); return; }
    let mark = box.nextElementSibling;
    if (!mark || !mark.classList.contains("koolezt-actions-mark")) { mark = document.createElement("div"); mark.className = "koolezt-actions-mark"; mark.setAttribute("aria-hidden", "true"); box.after(mark); }
    // The box floats when it sits above its own place (the mark right after it), whatever the screen height says.
    const style = getComputedStyle(box), now = box.getBoundingClientRect();
    const ownBottomNow = mark.getBoundingClientRect().top - (parseFloat(style.marginBottom) || 0), lifted = ownBottomNow - now.bottom > 2;
    if (lifted) line = now.bottom + (parseFloat(style.bottom) || 0);
    if (!html.classList.contains(FLOATING)) { full = box.offsetHeight; html.classList.toggle(FLOATING, lifted); return; }
    // Floating (Add to cart hidden): it stays so while the box, with Add to cart back, would still be off its own place.
    const ownBottom = ownBottomNow + Math.max(0, full - box.offsetHeight);
    html.classList.toggle(FLOATING, ownBottom > (line ?? window.innerHeight) + 1);
  };
  const queue = () => { if (!queued) { queued = true; window.requestAnimationFrame(update); } };
  window.addEventListener("scroll", queue, { passive: true });
  window.addEventListener("resize", () => { line = null; queue(); });
  window.setInterval(queue, 1000);
  queue();
})();
